(() => {
  if (customElements.get('signup-popup')) return;
  let stylesheet;
  class SignupPopup extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.storageKey = `peb:signup:${this.dataset.source}`;
      // Several popup sections (e.g. default and sale), each with its own rules and link (#signup-popup, #sale-popup, ...)
      this.hash = `#${this.dataset.hash || 'signup-popup'}`;
      const popups = [...document.querySelectorAll('signup-popup')];
      this.primary = popups[0] === this;
      // If two popups share a link, only the first one answers it
      this.ownsHash = popups.find(popup => `#${popup.dataset.hash || 'signup-popup'}` === this.hash) === this;
      this.rules = {};
      this.shownThisPage = false;
      this.trackPages();
      document.addEventListener('click', event => {
        const link = event.composedPath().find(node => node instanceof Element && node.matches('a[href]'));
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || link.target && link.target !== '_self') return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.hash !== this.hash || !this.ownsHash) return;
        event.preventDefault();
        this.open(link);
      }, options);
      window.addEventListener('hashchange', () => { if (this.ownsHash && location.hash === this.hash) this.open(); }, options);
      // peb:signup:open opens the first popup, or the one whose link matches event.detail.hash
      document.addEventListener('peb:signup:open', event => {
        const hash = event.detail?.hash && `#${String(event.detail.hash).replace(/^#/, '')}`;
        if (hash ? hash === this.hash && this.ownsHash : this.primary) this.open();
      }, options);
      document.addEventListener('shopify:section:select', event => {
        if (event.detail.sectionId === this.dataset.sectionId) this.open();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (event.detail.sectionId === this.dataset.sectionId) this.open();
      }, options);
      if (this.ownsHash && location.hash === this.hash) this.open();
      else this.setupRules();
    }
    disconnectedCallback() {
      this.controller.abort();
      clearTimeout(this.timer);
      clearTimeout(this.delayTimer);
      clearTimeout(this.copyTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.dialog?.close();
    }
    suppressed() {
      try {
        const state = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
        return this.dataset.suppressSubmitted !== 'false' && (state?.subscribed || state?.clicked) || state?.until > Date.now();
      } catch { return false; }
    }
    remember(subscribed = false, clicked = false) {
      try {
        const previous = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
        localStorage.setItem(this.storageKey, JSON.stringify({
          subscribed: Boolean(previous?.subscribed || subscribed), clicked: Boolean(previous?.clicked || clicked),
          until: Date.now() + Number(this.dataset.frequency) * 86400000
        }));
      } catch { /* Storage can be unavailable in private browsing. */ }
    }
    trackPages() {
      try {
        const key = `${this.storageKey}:pages`;
        const paths = new Set(JSON.parse(sessionStorage.getItem(key) || '[]'));
        paths.add(location.pathname);
        this.pagesViewed = paths.size;
        sessionStorage.setItem(key, JSON.stringify([...paths]));
      } catch { this.pagesViewed = 1; }
    }
    deviceAllowed() {
      const device = matchMedia('(max-width: 699px)').matches ? 'mobile' : 'desktop';
      return !this.dataset.devices || this.dataset.devices === 'all' || this.dataset.devices === device;
    }
    setupRules() {
      if (window.Shopify?.designMode || this.dataset.autoOpen !== 'true' || this.dataset.customer === 'true' || this.suppressed() || this.dataset.displayMode === 'custom') return;
      const options = { signal: this.controller.signal };
      this.rules.pages = this.pagesViewed >= Number(this.dataset.pageCount);
      if (this.dataset.triggerDelay !== 'false') {
        this.delayTimer = setTimeout(() => { this.rules.delay = true; this.autoOpen(); }, Number(this.dataset.delay) * 1000);
      }
      if (this.dataset.triggerScroll === 'true') {
        const updateScroll = () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => {
            const distance = document.documentElement.scrollHeight - innerHeight;
            const progress = distance > 0 ? scrollY / distance * 100 : 100;
            this.rules.scroll ||= progress >= Number(this.dataset.scrollPercent);
            this.autoOpen();
          });
        };
        window.addEventListener('scroll', updateScroll, { ...options, passive: true });
        window.addEventListener('load', updateScroll, options);
        updateScroll();
      }
      if (this.dataset.triggerExit === 'true') document.addEventListener('mouseout', event => {
        if (event.relatedTarget === null && event.clientY <= 0 && matchMedia('(min-width: 700px) and (pointer: fine)').matches) {
          this.rules.exit = true;
          this.autoOpen();
        }
      }, options);
      window.addEventListener('resize', () => this.autoOpen(), options);
      this.autoOpen();
    }
    rulesSatisfied() {
      if (this.dataset.displayMode === 'immediately') return true;
      if (this.dataset.displayMode === 'custom') return false;
      const enabled = ['delay', 'exit', 'scroll', 'pages'].filter(rule => {
        const value = this.dataset[`trigger${rule[0].toUpperCase()}${rule.slice(1)}`];
        return rule === 'delay' ? value !== 'false' : value === 'true';
      });
      return enabled.length > 0 && (this.dataset.ruleMatch === 'all' ? enabled.every(rule => this.rules[rule]) : enabled.some(rule => this.rules[rule]));
    }
    autoOpen() {
      // One popup per page view: skip if any signup popup has already opened on this page
      if (window.Shopify?.designMode || this.dataset.autoOpen !== 'true' || this.dataset.customer === 'true' || this.shownThisPage || window.pebSignupShown || this.suppressed() || !this.deviceAllowed() || !this.rulesSatisfied()) return;
      clearTimeout(this.timer);
      const blocking = [...document.querySelectorAll('dialog[open], [aria-modal="true"], privacy-banner[open]')].some(node => node.getClientRects().length);
      if (document.hidden || blocking) {
        this.timer = setTimeout(() => this.autoOpen(), 2000);
        return;
      }
      this.open();
    }
    async open(trigger) {
      if (this.opening || this.dialog?.open || !window.Shopify?.designMode && !this.deviceAllowed()) return;
      this.opening = true;
      this.querySelector('[data-signup-load-error]')?.remove();
      try {
        if (!stylesheet) stylesheet = new Promise((resolve, reject) => {
          const link = document.createElement('link');
          link.rel = 'stylesheet'; link.href = this.dataset.styleUrl;
          link.onload = resolve;
          link.onerror = () => { link.remove(); stylesheet = null; reject(new Error('Stylesheet unavailable')); };
          document.head.append(link);
        });
        await stylesheet;
        if (!this.isConnected || !window.Shopify?.designMode && !this.deviceAllowed()) return;
        if (!this.dialog) {
          this.append(this.querySelector('template').content.cloneNode(true));
          this.dialog = this.querySelector('dialog');
          this.form = this.querySelector('form');
          const options = { signal: this.controller.signal };
          this.dialog.addEventListener('click', event => {
            if (event.target.closest('[data-signup-copy]')) {
              this.copyCode();
              return;
            }
            if (event.target.closest('[data-signup-url]')) {
              if (this.dataset.preview !== 'true' && !window.Shopify?.designMode) this.remember(false, true);
              this.dialog.close();
              return;
            }
            const bounds = this.dialog.getBoundingClientRect();
            const outside = event.target === this.dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom);
            const closeOutside = matchMedia('(max-width: 699px)').matches ? this.dataset.closeOutsideMobile : this.dataset.closeOutsideDesktop;
            if (event.target.closest('[data-signup-close]') || outside && closeOutside !== 'false') this.dialog.close();
          }, options);
          this.dialog.addEventListener('close', () => {
            if (!this.submitted) this.remember();
            if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
          }, options);
          this.form.addEventListener('submit', event => this.submit(event), options);
        }
        this.trigger = trigger || document.activeElement;
        this.dialog.showModal();
        this.shownThisPage = true;
        window.pebSignupShown = true;
        clearTimeout(this.timer);
        clearTimeout(this.delayTimer);
        this.dialog.scrollTop = 0;
        const image = this.querySelector('img[data-src]');
        if (image) { image.src = image.dataset.src; delete image.dataset.src; }
      } catch {
        const error = document.createElement('p');
        error.dataset.signupLoadError = '';
        error.setAttribute('role', 'alert');
        error.textContent = this.dataset.error;
        this.append(error);
      } finally { this.opening = false; }
    }
    async copyCode() {
      const button = this.querySelector('[data-signup-copy]');
      const code = this.querySelector('[data-signup-code]');
      const status = this.querySelector('[data-signup-copy-status]');
      if (!button || !code || button.disabled) return;
      clearTimeout(this.copyTimer);
      button.textContent = button.dataset.copyLabel;
      button.disabled = true;
      status.textContent = '';
      try {
        await navigator.clipboard.writeText(code.textContent.trim());
        button.textContent = button.dataset.copiedLabel;
        status.classList.add('sr-only');
        status.textContent = button.dataset.copiedLabel;
        this.copyTimer = setTimeout(() => { button.textContent = button.dataset.copyLabel; }, 2500);
      } catch {
        const range = document.createRange();
        range.selectNodeContents(code);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        status.classList.remove('sr-only');
        status.textContent = button.dataset.copyError;
      } finally { button.disabled = false; }
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;
      this.busy = true;
      const button = this.form.querySelector('[type="submit"]');
      const error = this.querySelector('[data-signup-error]');
      button.disabled = true; button.setAttribute('aria-busy', 'true'); error.hidden = true;
      try {
        const email = this.form.elements.email.value.trim();
        const concern = this.form.querySelector('[name="concern"]:checked');
        const label = concern?.closest('label').querySelector('span').textContent.trim() || '';
        const profile = { type: 'profile', attributes: { email, properties: {
          'Signup Source': this.dataset.source,
          'Submitted Signup Popup': true,
          'Signup Popup Last Submitted At': new Date().toISOString(),
          'Storefront Language': this.dataset.language,
          'Storefront Country': this.dataset.country,
          ...(concern?.value ? { 'Skin Quiz main_concern': concern.value } : {})
        } } };
        const preview = this.dataset.preview === 'true' || window.Shopify?.designMode;
        if (!preview) {
          if (!this.dataset.companyId || !this.dataset.listId) throw new Error('Missing Klaviyo settings');
          const post = async (endpoint, data) => {
            const response = await fetch(`https://a.klaviyo.com/client/${endpoint}?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
              method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-10-15' },
              body: JSON.stringify({ data }), signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(12000)])
            });
            if (!response.ok) throw new Error('Klaviyo request failed');
          };
          // Set shared concern properties before list entry can trigger a welcome flow.
          await post('profiles', { ...profile, meta: { patch_properties: { append: { 'Signup Sources': [this.dataset.source] } } } });
          if (this.subscriptionEmail !== email) {
            await post('subscriptions', {
              type: 'subscription',
              attributes: { custom_source: this.dataset.source, profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } },
              relationships: { list: { data: { type: 'list', id: this.dataset.listId } } }
            });
            this.subscriptionEmail = email;
          }
          const submissionKey = `${email}|${concern?.value || ''}`;
          if (this.submissionKey !== submissionKey) {
            this.submissionKey = submissionKey;
            this.submissionId = crypto.randomUUID();
          }
          await post('events', { type: 'event', attributes: {
            unique_id: this.submissionId,
            metric: { data: { type: 'metric', attributes: { name: 'Signup Popup Submitted' } } },
            profile: { data: profile },
            properties: {
              'Signup Source': this.dataset.source, 'main_concern': concern?.value || '', 'Concern Label': label || '',
              'Marketing Opt In': true, 'Marketing Consent Method': 'form_submission',
              'Marketing Consent Text': this.querySelector('.signup-dialog__consent').textContent.trim(),
              'Discount Code': this.querySelector('[data-signup-code]')?.textContent.trim() || ''
            }
          } });
        }
        this.submitted = true;
        if (!preview) this.remember(true);
        this.querySelector('[data-signup-form-panel]').hidden = true;
        const success = this.querySelector('[data-signup-success]');
        success.hidden = false;
        const heading = success.querySelector('h2');
        heading.id = `SignupSuccess-${this.dataset.sectionId}`;
        this.dialog.setAttribute('aria-labelledby', heading.id);
        this.dialog.scrollTop = 0;
        success.focus({ preventScroll: true });
      } catch {
        error.textContent = this.dataset.error; error.hidden = false;
      } finally {
        this.busy = false; button.disabled = false; button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('signup-popup', SignupPopup);
})();
